/**
 * Financial Indicators Service
 *
 * Fetches financial indicators (ratios) from the backend API and transforms them
 * for display in the Report Editor bottom panel "Indicadores Financieros" tab.
 */

import { get } from './api';
import {
  FinancialIndicatorRow,
  FinancialIndicatorsData,
  FinancialIndicatorsResponse,
} from '../types/balanceSheet';
import { calculateDisplayPeriods } from './balanceSheetService';

// Indicators that should be displayed as plain ratios (not percentages)
const RATIO_INDICATORS = ['S2', 'S4'];

// Category display order
const CATEGORY_ORDER = [
  'Solvencia',
  'Calidad de Activos',
  'Eficiencia y Gestion',
  'Rentabilidad',
  'Liquidez',
  'Sensibilidad Tasa Interes',
];

/**
 * Format indicator value based on indicator type
 * S2 and S4 are ratios, all others are percentages
 */
export function formatIndicatorValue(
  value: number | null | undefined,
  indicatorId: string
): string {
  if (value === null || value === undefined) return '-';

  if (RATIO_INDICATORS.includes(indicatorId)) {
    // Display as plain ratio (e.g., 2.67)
    return value.toFixed(2);
  }

  // Display as percentage (e.g., 23.03%)
  // Values from BigQuery are already decimals (0.2303)
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Format period header for display (e.g., "2024-12" → "Dic-24")
 */
export function formatPeriodHeader(period: string): string {
  const [year, month] = period.split('-');
  const monthNames: Record<string, string> = {
    '01': 'Ene',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Abr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Ago',
    '09': 'Sep',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dic',
  };
  return `${monthNames[month] || month}-${year.slice(2)}`;
}

/**
 * Fetch financial indicators from the backend
 */
export async function fetchFinancialIndicators(
  institutionId: string,
  reportPeriod: string
): Promise<FinancialIndicatorsData> {
  // Use same period calculation as balance sheet for consistency
  const periodConfig = calculateDisplayPeriods(reportPeriod);

  // Call backend API
  const response = await get<FinancialIndicatorsResponse>(
    `/api/financial-indicators/${institutionId}?periods=${periodConfig.allPeriodsToFetch.join(',')}`
  );

  if (!response.success || !response.data) {
    return {
      institution_name: institutionId,
      periods: periodConfig.allDisplayPeriods,
      categories: [],
    };
  }

  // Transform data: group by category
  const categoryMap = new Map<string, FinancialIndicatorRow[]>();

  for (const indicator of response.data) {
    const category = indicator.indicator_category;
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push({
      indicator_id: indicator.indicator_id,
      indicator_name: indicator.indicator_name,
      indicator_category: indicator.indicator_category,
      values: indicator.values,
    });
  }

  // Sort categories by defined order
  const categories = CATEGORY_ORDER.filter((cat) => categoryMap.has(cat)).map(
    (name) => ({
      name,
      indicators: categoryMap.get(name) || [],
    })
  );

  return {
    institution_name: response.institution_name,
    periods: periodConfig.allDisplayPeriods,
    categories,
  };
}

/**
 * Check if an indicator is a ratio (not percentage)
 */
export function isRatioIndicator(indicatorId: string): boolean {
  return RATIO_INDICATORS.includes(indicatorId);
}

/**
 * Get the category order for display
 */
export function getCategoryOrder(): string[] {
  return [...CATEGORY_ORDER];
}
